import React, { useState, useEffect } from 'react';
import { X, Star, Minus, Plus } from 'lucide-react';
import { formatVND } from '../../utils/formatters';

export default function QuickViewModal({ product, onClose, addToCart }) {
  const [qvQuantity, setQvQuantity] = useState(1);
  const [qvSelectedColor, setQvSelectedColor] = useState(null);
  const [qvSelectedSize, setQvSelectedSize] = useState(null);

  // Cập nhật lại state khi đổi sản phẩm được chọn
  useEffect(() => {
    setQvQuantity(1);
    setQvSelectedColor(product?.colors?.[0] || null);
    setQvSelectedSize(product?.sizes?.[0] || null);
  }, [product]);

  if (!product) return null;

  // Lấy ảnh thumbnail chính xác từ Backend (nếu không có thì dùng ảnh mảng hoặc ảnh mặc định)
  const imageUrl = product.thumbnail || product.images?.[0] || 'https://via.placeholder.com/300';

  // Lấy tên danh mục chính xác từ Backend DTO
  const categoryName = product.categoryName || product.category?.name || product.category || 'Chưa phân loại';

  // Kiểm tra tồn kho
  const isOutOfStock = product.stock === 0 || product.status === 'OUT_OF_STOCK';

  return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-3xl max-w-2xl w-full p-6 relative overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Nút Đóng Modal */}
          <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition z-10 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Cột Trái: Hiển thị hình ảnh sản phẩm từ Backend */}
            <div className="aspect-square bg-slate-100 rounded-2xl overflow-hidden relative flex items-center justify-center">
              <img
                  src={imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover"
              />
              {isOutOfStock && (
                  <span className="absolute top-3 left-3 bg-rose-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow">
                HẾT HÀNG
              </span>
              )}
            </div>

            {/* Cột Phải: Thông tin sản phẩm từ DB */}
            <div className="space-y-4 flex flex-col justify-between">
              <div>
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest">
                {categoryName}
              </span>
                <h3 className="text-xl font-bold text-slate-900 mt-1">{product.name}</h3>

                {/* Đánh giá Sao */}
                <div className="flex items-center space-x-1 mt-1 text-amber-400">
                  {[...Array(5)].map((_, i) => (
                      <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${
                              i < Math.floor(product.star || 5) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'
                          }`}
                      />
                  ))}
                  <span className="text-xs font-bold text-slate-500 ml-1">
                  ({product.star ? `${product.star}.0` : '5.0'})
                </span>
                </div>

                {/* Giá bán */}
                <div className="flex items-center space-x-2 mt-2">
                <span className="text-2xl font-extrabold text-slate-900">
                  {product.price ? (formatVND ? formatVND(product.price) : `${product.price.toLocaleString()} VNĐ`) : '0 VNĐ'}
                </span>
                  {product.originalPrice > product.price && (
                      <span className="text-sm text-slate-400 line-through">
                    {formatVND ? formatVND(product.originalPrice) : `${product.originalPrice.toLocaleString()} VNĐ`}
                  </span>
                  )}
                </div>

                {/* Mô tả chi tiết sản phẩm */}
                <p className="text-xs text-slate-500 mt-3 leading-relaxed max-h-28 overflow-y-auto whitespace-pre-line">
                  {product.description && product.description.trim() !== ''
                      ? product.description
                      : 'Chưa có mô tả chi tiết cho sản phẩm này.'}
                </p>
              </div>

              {/* Lựa chọn Màu sắc, Kích thước & Số lượng */}
              <div className="space-y-3 border-t border-slate-100 pt-3">
                {product.colors && product.colors.length > 0 && (
                    <div>
                      <span className="text-xs font-bold text-slate-700 block mb-1.5">Màu sắc</span>
                      <div className="flex space-x-2">
                        {product.colors.map((color) => (
                            <button
                                key={color}
                                onClick={() => setQvSelectedColor(color)}
                                className={`w-6 h-6 rounded-full border-2 transition cursor-pointer ${
                                    qvSelectedColor === color ? 'ring-2 ring-indigo-600 ring-offset-1' : 'border-slate-200'
                                }`}
                                style={{ backgroundColor: color }}
                            />
                        ))}
                      </div>
                    </div>
                )}

                {product.sizes && product.sizes.length > 0 && (
                    <div>
                      <span className="text-xs font-bold text-slate-700 block mb-1.5">Kích thước</span>
                      <div className="flex flex-wrap gap-1.5">
                        {product.sizes.map((size) => (
                            <button
                                key={size}
                                onClick={() => setQvSelectedSize(size)}
                                className={`px-3 py-1 text-xs font-bold rounded-lg border transition cursor-pointer ${
                                    qvSelectedSize === size
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                }`}
                            >
                              {size}
                            </button>
                        ))}
                      </div>
                    </div>
                )}

                {/* Tăng / Giảm Số lượng */}
                <div>
                  <span className="text-xs font-bold text-slate-700 block mb-1.5">Số lượng</span>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center border border-slate-200 rounded-xl">
                      <button
                          disabled={qvQuantity <= 1 || isOutOfStock}
                          onClick={() => setQvQuantity(Math.max(1, qvQuantity - 1))}
                          className="p-2 hover:bg-slate-100 text-slate-600 rounded-l-xl disabled:opacity-40 cursor-pointer"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="px-3 text-xs font-bold text-slate-800">{qvQuantity}</span>
                      <button
                          disabled={isOutOfStock || (product.stock && qvQuantity >= product.stock)}
                          onClick={() => setQvQuantity(qvQuantity + 1)}
                          className="p-2 hover:bg-slate-100 text-slate-600 rounded-r-xl disabled:opacity-40 cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nút Thêm Vào Giỏ Hàng */}
              <button
                  disabled={isOutOfStock}
                  onClick={() => {
                    if (isOutOfStock) return;
                    addToCart(product, qvQuantity, qvSelectedColor, qvSelectedSize);
                    onClose();
                  }}
                  className={`w-full font-bold py-3 rounded-xl transition text-sm shadow-lg cursor-pointer ${
                      isOutOfStock
                          ? 'bg-slate-200 text-slate-400 shadow-none cursor-not-allowed'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20 active:scale-95'
                  }`}
              >
                {isOutOfStock ? 'Sản Phẩm Đã Hết Hàng' : 'Thêm Vào Giỏ Hàng'}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
}